import React from 'react';

const Section = ({ number, title, children }) => (
  <section className="mb-10">
    <h2 className="text-xl font-semibold text-gray-900 mb-4">{number}. {title}</h2>
    {children}
  </section>
);

const P = ({ children }) => (
  <p className="text-gray-700 leading-relaxed mb-3">{children}</p>
);

const Ul = ({ items }) => (
  <ul className="list-disc pl-6 text-gray-700 mb-3 space-y-1">
    {items.map((item, i) => <li key={i}>{item}</li>)}
  </ul>
);

const Privacy = () => (
  <div className="min-h-screen bg-white">
    <nav className="border-b border-gray-200 bg-white">
      <div className="max-w-7xl mx-auto px-6 py-4 flex justify-between items-center">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 stripe-gradient rounded-lg flex items-center justify-center">
            <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 20 20">
              <path d="M4 3a2 2 0 00-2 2v1a1 1 0 001 1h14a1 1 0 001-1V5a2 2 0 00-2-2H4zM3 8a1 1 0 011-1h12a1 1 0 011 1v5a2 2 0 01-2 2H6a2 2 0 01-2-2V8z" />
            </svg>
          </div>
          <span className="text-2xl font-semibold text-gray-900">Journal Coffee</span>
        </div>
        <a href="/" className="text-gray-600 hover:text-gray-900 font-medium">← Back to Home</a>
      </div>
    </nav>

    <main className="max-w-3xl mx-auto px-6 py-12">
      <h1 className="text-4xl font-bold text-gray-900 mb-3">Privacy Policy</h1>
      <p className="text-gray-500 mb-10">Last updated: June 10, 2026</p>

      <div>
        <Section number="1" title="What Information We Collect">
          <P>We collect only what is necessary to provide the Service.</P>

          <p className="text-sm font-semibold text-gray-800 mb-2 mt-4">Account information</p>
          <Ul items={[
            'Email address — provided when you create an account',
            'Authentication tokens — managed by Firebase Authentication (Google)',
          ]} />

          <p className="text-sm font-semibold text-gray-800 mb-2 mt-4">Subscription and billing</p>
          <Ul items={[
            'Subscription status (active, trialing, cancelled) — stored in our database',
            'Stripe customer ID and subscription ID — references to your Stripe account',
            'Payment card details are never stored by Journal Coffee; they are handled entirely by Stripe',
          ]} />

          <p className="text-sm font-semibold text-gray-800 mb-2 mt-4">Usage data</p>
          <Ul items={[
            'Search queries entered in the research library',
            'Standard server logs (IP address, browser type, pages visited, timestamps) collected automatically by Firebase',
          ]} />
        </Section>

        <Section number="2" title="How We Use Your Information">
          <Ul items={[
            'To create and manage your account',
            'To process payments and manage your subscription through Stripe',
            'To control access to premium content based on subscription status',
            'To deliver search results via Algolia',
            'To send transactional emails (subscription confirmation, trial expiry reminders, billing receipts)',
            'To respond to support requests',
            'To improve and debug the Service',
          ]} />
          <P>
            We do not use your data for advertising and we do not sell it to any third party.
          </P>
        </Section>

        <Section number="3" title="Third-Party Services and Data Sharing">
          <P>
            Journal Coffee uses the following third-party services that may receive or process your data:
          </P>

          <div className="space-y-4 mb-3">
            <div className="bg-gray-50 rounded-lg p-4">
              <p className="font-semibold text-gray-800 mb-1">Stripe</p>
              <p className="text-gray-600 text-sm">Processes all payments. Receives your email address and payment details. Stripe stores and processes card information under their own PCI-compliant environment. <a href="https://stripe.com/privacy" className="text-indigo-600 hover:underline" target="_blank" rel="noopener noreferrer">Stripe Privacy Policy →</a></p>
            </div>
            <div className="bg-gray-50 rounded-lg p-4">
              <p className="font-semibold text-gray-800 mb-1">Google Firebase (Google LLC)</p>
              <p className="text-gray-600 text-sm">Provides authentication, database storage (Firestore), and file storage (Cloud Storage). Your email, subscription status, and account data are stored in Firebase infrastructure hosted in the United States. <a href="https://firebase.google.com/support/privacy" className="text-indigo-600 hover:underline" target="_blank" rel="noopener noreferrer">Firebase Privacy →</a></p>
            </div>
            <div className="bg-gray-50 rounded-lg p-4">
              <p className="font-semibold text-gray-800 mb-1">Algolia</p>
              <p className="text-gray-600 text-sm">Powers the search functionality in your research library. Search queries you enter are sent to Algolia's servers to retrieve results. <a href="https://www.algolia.com/policies/privacy" className="text-indigo-600 hover:underline" target="_blank" rel="noopener noreferrer">Algolia Privacy →</a></p>
            </div>
            <div className="bg-gray-50 rounded-lg p-4">
              <p className="font-semibold text-gray-800 mb-1">Amazon Web Services</p>
              <p className="text-gray-600 text-sm">Used for content delivery (audio and PDF files served via CloudFront). Delivery logs may include your IP address.</p>
            </div>
          </div>

          <P>
            We also share your information when required by law or to protect the rights, property, or safety of Journal Coffee, our users, or the public.
          </P>
        </Section>

        <Section number="4" title="Data Storage and Security">
          <P>
            Your account data is stored in Google Firebase (Firestore), hosted in Google Cloud infrastructure. Audio and PDF files are stored in Firebase Cloud Storage and served via Amazon CloudFront.
          </P>
          <P>
            We implement appropriate technical measures to protect your data, including encrypted connections (HTTPS/TLS), Firebase security rules that restrict data access to authenticated users with active subscriptions, and no direct client-side write access to production data.
          </P>
          <P>
            No method of transmission or storage is 100% secure. If you become aware of a security concern, please contact us at <a href="mailto:support@journalcoffee.com" className="text-indigo-600 hover:underline">support@journalcoffee.com</a>.
          </P>
        </Section>

        <Section number="5" title="Data Retention">
          <Ul items={[
            'Active accounts: your data is retained for as long as your account exists',
            'Cancelled subscriptions: account data is retained for 90 days after cancellation, then deleted',
            'Payment records: Stripe retains transaction records as required by financial regulations; we retain Stripe customer and subscription IDs for the same period',
            'Server logs: retained for up to 30 days',
          ]} />
          <P>
            You may request deletion of your account and associated data at any time by emailing <a href="mailto:support@journalcoffee.com" className="text-indigo-600 hover:underline">support@journalcoffee.com</a>.
          </P>
        </Section>

        <Section number="6" title="Cookies and Local Storage">
          <P>
            Journal Coffee uses the following browser storage:
          </P>
          <Ul items={[
            'Firebase Authentication tokens — stored in browser local storage to keep you signed in across sessions. These are cleared when you sign out.',
            'No third-party advertising or tracking cookies are used.',
          ]} />
          <P>
            You can clear these by signing out of the Service or clearing your browser's local storage.
          </P>
        </Section>

        <Section number="7" title="Your Rights">
          <P>Depending on your location, you may have the right to:</P>
          <Ul items={[
            'Access the personal data we hold about you',
            'Correct inaccurate data',
            'Request deletion of your account and data',
            'Object to or restrict certain processing',
            'Receive a copy of your data in a portable format',
          ]} />
          <P>
            To exercise any of these rights, contact us at <a href="mailto:support@journalcoffee.com" className="text-indigo-600 hover:underline">support@journalcoffee.com</a>. We will respond within 30 days.
          </P>
          <P>
            If you are located in the European Economic Area (EEA) or United Kingdom, you also have the right to lodge a complaint with your local data protection authority.
          </P>
        </Section>

        <Section number="8" title="Children's Privacy">
          <P>
            Journal Coffee is not directed at children under the age of 13. We do not knowingly collect personal information from children under 13. If you believe a child has provided us with personal information, please contact us and we will delete it promptly.
          </P>
        </Section>

        <Section number="9" title="Changes to This Policy">
          <P>
            We may update this Privacy Policy from time to time. Material changes will be communicated by email or by a notice on the Service. The "Last updated" date at the top of this page reflects the most recent revision.
          </P>
        </Section>

        <Section number="10" title="Contact">
          <P>
            For questions, data requests, or privacy concerns, contact us at <a href="mailto:support@journalcoffee.com" className="text-indigo-600 hover:underline">support@journalcoffee.com</a>.
          </P>
        </Section>
      </div>
    </main>

    <footer className="border-t border-gray-100 py-8 px-6 text-center">
      <div className="flex justify-center gap-6 text-sm text-gray-500">
        <a href="/terms" className="hover:text-gray-700 transition-colors">Terms of Service</a>
        <a href="/" className="hover:text-gray-700 transition-colors">Home</a>
      </div>
    </footer>
  </div>
);

export default Privacy;
